import { PropsWithChildren, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { PopupState } from "@inferno/renderer-shared-core";
import { ButtonKind, FauxButton } from "../../../../../core/ui";
import { CoastEmailUpdateComplete } from "./CoastEmailUpdateComplete.component";
import { CoastForgotPassword } from "./CoastForgotPassword.component";
import { CoastGetPasswordHint } from "./CoastGetPasswordHint.component";
import { CoastLoginForm } from "./CoastLoginForm.component";
import "./CoastLoginModal.style.scss";
import { CoastResetPassword } from "./CoastResetPassword.component";

type SubMenu = "forgotPassword" | "resetPassword" | "updateEmailComplete" | "passwordHint" | null;

const isSubMenu = (v?: string | null): v is SubMenu =>
  v === null || (!!v && ["forgotPassword", "resetPassword", "updateEmailComplete", "passwordHint"].includes(v));

interface LoginFormInputs {
  password: string;
  email: string;
}

interface CoastLoginModalProps {
  viewData: PopupState["data"];
}

interface CoastLoginModalBaseProps {
  includeNotAMember?: boolean;
  header: string;
}

function CoastLoginModalBase({
  includeNotAMember = true,
  children,
  header,
}: PropsWithChildren<CoastLoginModalBaseProps>) {
  return (
    <div className="component-coast-login-modal">
      <header>
        <img src="/static/assets/coast/logo-black-stacked.png" height="110" width="150" alt="" />

        <h2>{header}</h2>

        {includeNotAMember ? (
          <>
            <h3>Not a Coast Insider Member?&nbsp;</h3>
            <FauxButton kind={ButtonKind.CUSTOMIZED} to="/sign-up/" className="coast-button-text">
              Sign up
            </FauxButton>
          </>
        ) : null}
      </header>

      <main className="dialog-content">{children}</main>

      <footer>
        <hr className="divider" />

        <FauxButton kind={ButtonKind.CUSTOMIZED} to="/optout/" className="coast-button-text">
          Do Not Sell or Share My Personal Information
        </FauxButton>

        <FauxButton kind={ButtonKind.CUSTOMIZED} to="/coastinsider/" className="coast-button-text">
          Learn more about Coast Insider
        </FauxButton>
      </footer>
    </div>
  );
}

export function CoastLoginModal({ viewData }: CoastLoginModalProps) {
  const loginFormMethods = useForm<LoginFormInputs>();

  const [subMenu, setSubMenu] = useState<SubMenu>(null);

  useEffect(() => {
    if (viewData && isSubMenu(viewData.subMenu)) {
      setSubMenu(viewData.subMenu);
    }
  }, [viewData]);

  switch (subMenu) {
    case "forgotPassword":
      return (
        <CoastLoginModalBase header="Forgot Password">
          <CoastForgotPassword goBack={() => setSubMenu(null)} email={loginFormMethods.getValues("email")} />
        </CoastLoginModalBase>
      );
    case "resetPassword":
      return (
        <CoastLoginModalBase header="Reset Password">
          <CoastResetPassword
            passwordToken={viewData?.passwordToken}
            forgotPassword={() => setSubMenu("forgotPassword")}
            goBack={() => setSubMenu(null)}
          />
        </CoastLoginModalBase>
      );
    case "passwordHint":
      return (
        <CoastLoginModalBase header="Get Password Hint">
          <CoastGetPasswordHint email={loginFormMethods.getValues("email")} goBack={() => setSubMenu(null)} />
        </CoastLoginModalBase>
      );
    case "updateEmailComplete":
      return (
        <CoastLoginModalBase header="Updating Your Email Address" includeNotAMember={false}>
          <CoastEmailUpdateComplete emailToken={viewData?.emailToken} />
        </CoastLoginModalBase>
      );
    default:
      return (
        <CoastLoginModalBase header="Log In">
          <FormProvider {...loginFormMethods}>
            <CoastLoginForm
              forgotPassword={() => setSubMenu("forgotPassword")}
              passwordHint={() => setSubMenu("passwordHint")}
            />
          </FormProvider>
        </CoastLoginModalBase>
      );
  }
}
